import { useQuery } from "@tanstack/react-query"
import {
  ArrowLeftRight,
  Bell,
  BookMarked,
  BookOpen,
  Boxes,
  Building,
  Building2,
  ChartColumn,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  ClipboardList,
  Compass,
  FileCheck,
  FileSignature,
  FileText,
  FolderOpen,
  Gauge,
  HandCoins,
  Handshake,
  IdCard,
  KeyRound,
  Landmark,
  LifeBuoy,
  ListChecks,
  MessagesSquare,
  Network,
  Package,
  PackageSearch,
  Palette,
  Percent,
  ReceiptText,
  ScrollText,
  Ship,
  SquareDashed,
  Tags,
  Target,
  TrendingUp,
  Truck,
  UserRound,
  Users,
  Wallet,
  Workflow,
} from "lucide-react"
import { motion } from "motion/react"
import { useState } from "react"
import { NavLink, useLocation } from "react-router"

import { api } from "@/api/cliente"
import { LogoSetieca } from "@/componentes/marca/LogoSetieca"
import { esCliente, esTransportista, tienePermiso } from "@/lib/autorizacion"
import { cn } from "@/lib/cn"
import { guardarAbiertas, guardarColapsado, leerAbiertas, leerColapsado } from "@/lib/secciones"
import { usarSesion } from "@/lib/sesion"
import { ENTRADA, LAMINA_SESION, MARCA_SESION, VIAJE_LAMINA } from "@/lib/transiciones"
import { NOMBRE_PORTAL_TRANSPORTE } from "@/lib/version"
import { CONSULTA_MENU, type EntradaMenu } from "./menu"

/**
 * El menú.
 *
 * ⚠ **Para el personal interno sale de la BASE, no de este archivo.** `GET /menu`
 * devuelve las entradas activas para las que quien pregunta tiene permiso, ya
 * ordenadas. Antes era un arreglo aquí dentro, y ofrecer o esconder un módulo
 * significaba editar código y volver a desplegar.
 *
 * ⚠ **Esconder una entrada NO cierra su ruta.** El servidor decide el permiso en
 * cada petición con `authz.can()`; esta lista dice qué se ofrece. Confundir las dos
 * cosas es lo que dejó veintinueve de las ochenta vistas del 2.0 alcanzables
 * escribiendo la URL.
 *
 * ⚠ **El importador NO usa este menú, y no es un descuido.** Un CLIENTE sí tiene
 * `expediente:ver` —lo necesita para los suyos— así que `/menu` le devolvería el
 * enlace a `/expedientes`, la bandeja del personal: mismas columnas, mismo lenguaje
 * operativo y un botón de avanzar que le respondería 404. Su sitio es `/portal`, que
 * enseña lo mismo en los términos en que él lo entiende, y por eso su navegación
 * sigue siendo una lista fija aquí.
 */

/**
 * Los iconos que este frontend conoce.
 *
 * ⚠ **El servidor manda el NOMBRE y aquí se resuelve.** No puede saber qué iconos
 * trae la versión desplegada, y mandar marcado desde una API sería peor. Un nombre
 * desconocido cae a uno genérico: una entrada sin icono se sigue pudiendo pulsar, y
 * reventar el menú entero por un icono es un precio absurdo.
 */
const ICONOS: Record<string, typeof Ship> = {
  ArrowLeftRight,
  Bell,
  BookMarked,
  BookOpen,
  Boxes,
  PackageSearch,
  Building,
  Building2,
  ChartColumn,
  ClipboardCheck,
  ClipboardList,
  Compass,
  FileCheck,
  FileSignature,
  FileText,
  // `FolderOpen` y `LifeBuoy` llegan del menú en la base —el Drive y la bandeja de
  // reportes—: sin declararlos aquí caían al icono genérico.
  FolderOpen,
  HandCoins,
  Handshake,
  IdCard,
  KeyRound,
  Landmark,
  LifeBuoy,
  ListChecks,
  MessagesSquare,
  Network,
  Palette,
  Gauge,
  Percent,
  ReceiptText,
  ScrollText,
  Ship,
  // `Tags` es el de Rotulación y faltaba: la entrada caía al icono genérico sin que
  // nada lo dijera. Ahora lo caza `src/modulos.test.ts`.
  Tags,
  Target,
  TrendingUp,
  Truck,
  // Los del menú del portal de transporte (25/10).
  Package,
  UserRound,
  Users,
  Wallet,
  Workflow,
}
type SeccionesMenu = { orden: string[]; sin_modulo: string[] }

/**
 * Las secciones en el orden del servidor, con los departamentos sin módulo intercalados
 * en su sitio y vacíos (28/10). Sin la respuesta todavía, las secciones tal como llegaron.
 * Exportada para probarla.
 */
export function conDepartamentosVacios(
  agrupadas: Map<string, EntradaMenu[]>,
  secciones: SeccionesMenu | undefined,
): Map<string, EntradaMenu[]> {
  if (!Array.isArray(secciones?.orden) || !Array.isArray(secciones?.sin_modulo)) return agrupadas
  const vacios = new Set(secciones.sin_modulo)
  const salida = new Map<string, EntradaMenu[]>()
  // Lo que no tiene sección va primero, como siempre.
  const sueltas = agrupadas.get("")
  if (sueltas) salida.set("", sueltas)
  for (const nombre of secciones.orden) {
    const grupo = agrupadas.get(nombre)
    if (grupo) salida.set(nombre, grupo)
    else if (vacios.has(nombre)) salida.set(nombre, [])
  }
  // Una sección creada desde accesos que el servidor no ordena va detrás, como antes.
  for (const [nombre, grupo] of agrupadas) if (!salida.has(nombre)) salida.set(nombre, grupo)
  return salida
}

const PORTAL: EntradaMenu[] = [
  {
    id: -1,
    clave: "portal",
    etiqueta: "Mis despachos",
    ruta: "/portal",
    icono: "Ship",
    seccion: "",
  },
  {
    id: -2,
    clave: "portal-estado",
    etiqueta: "Estado de cuenta",
    ruta: "/portal/estado-cuenta",
    icono: "ReceiptText",
    seccion: "",
  },
  {
    id: -3,
    clave: "portal-buzon",
    etiqueta: "Buzón",
    ruta: "/portal/buzon",
    icono: "MessagesSquare",
    seccion: "",
  },
]

/**
 * El menú del portal de transporte (25/10): fijo, como el del importador, porque el
 * intermediario no tiene módulos ni permisos que repartir —su cuenta sólo alcanza
 * `/api/v1/transporte/`—. Las rutas son las de `main.tsx` en el host `transporte.*`.
 */
const TRANSPORTE: EntradaMenu[] = [
  {
    id: -11,
    clave: "t-cargas",
    etiqueta: "Cargas asignadas",
    ruta: "/cargas",
    icono: "Package",
    seccion: "",
  },
  {
    id: -12,
    clave: "t-recibos",
    etiqueta: "Recibos",
    ruta: "/recibos",
    icono: "FileText",
    seccion: "",
  },
  {
    id: -13,
    clave: "t-empresas",
    etiqueta: "Empresas",
    ruta: "/empresas",
    icono: "Building2",
    seccion: "Catálogo",
  },
  {
    id: -14,
    clave: "t-vehiculos",
    etiqueta: "Vehículos",
    ruta: "/vehiculos",
    icono: "Truck",
    seccion: "Catálogo",
  },
  {
    id: -15,
    clave: "t-choferes",
    etiqueta: "Choferes",
    ruta: "/choferes",
    icono: "UserRound",
    seccion: "Catálogo",
  },
]

/** Qué permiso hace visible cada entrada del portal. Sigue siendo cosmético. */
const PERMISO_PORTAL: Record<string, string> = {
  portal: "expediente:ver",
  "portal-estado": "cxc:ver",
  "portal-buzon": "buzon:ver",
}

/**
 * Una sección del menú, con su título que la pliega.
 *
 * ⚠ **Es un `button`, no un `div` con `onClick`.** Lo que se pulsa se alcanza con el
 * teclado y se anuncia como control: `aria-expanded` dice si está abierta y
 * `aria-controls` ata el título a lo que gobierna. Un `div` clicable es invisible para
 * quien navega con lector.
 *
 * ⚠ **La altura NO se anima** (regla del frontend: sólo `transform` y `opacity`).
 * Animar `height` obliga al navegador a recalcular el diseño en cada fotograma, y en
 * una barra que se pliega y despliega a diario eso se nota. Lo que se mueve es el
 * chevron, que es un giro.
 *
 * ⚠ **Una sección plegada que contiene la ruta ACTUAL lo dice con un punto.** Sin esa
 * señal, quien pliega «Finanzas» estando en Tesorería pierde de vista dónde está: el
 * menú deja de contestar «¿en qué parte del sistema estoy?», que es la mitad de su
 * trabajo. No se despliega sola — plegarla fue una decisión suya.
 */
/**
 * Un departamento que todavía no tiene ningún módulo (28/10, minuta «Estructura de la
 * Compañía»): su nombre en el menú, sin entradas y sin nada que pulsar.
 *
 * ⚠ **No es un enlace ni una pantalla vacía**: lo pidió así el usuario —«crea el
 * departamento en el sidebar sin el módulo, no crees vistas vacías»—. Dice que el
 * departamento existe en la estructura; cuando tenga un módulo, sale como los demás.
 */
function DepartamentoSinModulo({ titulo }: { titulo: string }) {
  return (
    <div
      className="mt-2 mb-1 flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] font-semibold tracking-wider uppercase"
      style={{ color: "rgb(var(--marco-tinta) / 0.35)" }}
      title="Departamento sin módulos todavía"
    >
      <span aria-hidden={true} className="inline-block w-3 shrink-0" />
      <span className="flex-1">{titulo}</span>
      <span className="sr-only">(departamento sin módulos todavía)</span>
    </div>
  )
}

function Seccion({
  titulo,
  entradas,
  plegada,
  onAlternar,
}: {
  titulo: string
  entradas: EntradaMenu[]
  plegada: boolean
  onAlternar: () => void
}) {
  const { pathname } = useLocation()
  const idPanel = `menu-seccion-${titulo.replace(/\s+/g, "-").toLowerCase()}`
  // La misma regla que `NavLink`: la ruta actual, o una hija suya.
  const contieneActiva = entradas.some(
    (e) => pathname === e.ruta || pathname.startsWith(`${e.ruta}/`),
  )

  return (
    <div className="mb-1">
      <button
        type="button"
        onClick={onAlternar}
        aria-expanded={!plegada}
        aria-controls={idPanel}
        className="foco-cristal toque-cristal mt-2 mb-1 flex w-full items-center gap-1.5 rounded-[var(--radius-control)] px-2.5 py-1.5 text-[10px] font-semibold tracking-wider uppercase"
        // Blanco al 55 %, y no `--fg-muted`: ese gris está calibrado contra una
        // superficie del tema, y sobre el cristal oscuro se hunde.
        style={{ color: "rgb(var(--marco-tinta) / 0.55)" }}
      >
        <ChevronRight
          size={12}
          aria-hidden={true}
          className="shrink-0 transition-transform duration-150 motion-reduce:transition-none"
          style={{ transform: plegada ? undefined : "rotate(90deg)" }}
        />
        <span className="flex-1 text-left">{titulo}</span>
        {plegada && contieneActiva && (
          <span
            // El punto es el refuerzo; el nombre de la sección sigue ahí y es el dato.
            aria-hidden={true}
            className="size-1.5 shrink-0 rounded-full"
            style={{ background: "rgb(var(--marco-tinta) / 0.9)" }}
          />
        )}
        {plegada && contieneActiva && <span className="sr-only">(sección actual)</span>}
      </button>
      {!plegada && (
        <div id={idPanel}>
          {entradas.map((e) => (
            <Enlace key={e.clave} entrada={e} />
          ))}
        </div>
      )}
    </div>
  )
}

function Enlace({ entrada, soloIcono = false }: { entrada: EntradaMenu; soloIcono?: boolean }) {
  const Icono = ICONOS[entrada.icono] ?? SquareDashed
  return (
    <NavLink
      to={entrada.ruta}
      // Colapsada, el nombre sale al pasar el ratón y lo sigue leyendo el lector.
      title={soloIcono ? entrada.etiqueta : undefined}
      className={({ isActive }) =>
        cn(
          "foco-cristal flex items-center gap-2.5 rounded-[var(--radius-control)] py-2 text-sm transition-colors",
          soloIcono ? "justify-center px-0" : "px-2.5",
          // El vocabulario del cristal: la luz sube, nada se rellena. Ver
          // `.toque-cristal` y `.activo-cristal` en `globales.css`.
          isActive ? "activo-cristal font-medium" : "toque-cristal",
        )
      }
      // ⚠ **Tres señales y no una: color, fondo teñido y una barra a la izquierda.**
      // El fondo gris de antes servía con un acento claro; con el borgoña el ítem
      // activo se distinguía sólo por la tinta, y en una columna de catorce entradas
      // eso hay que buscarlo. La barra es la que se ve de reojo, que es como se lee
      // un menú lateral.
      // ⚠ **Sobre cristal oscuro las tres señales son otras, y por la misma razón
      // que las eligió.** `--primary-suave` y `--primary-texto` están calibrados
      // contra la superficie del TEMA; aquí el fondo es una fotografía tamizada y el
      // borgoña sobre vino oscuro no se separa de nada. Se conservan las tres
      // señales —fondo teñido, tinta y barra— cambiando el borgoña por luz, que es
      // lo único que contrasta con un oscuro sea cual sea la foto de detrás.
      // El fondo y el filo los pone `.activo-cristal`; aquí sólo queda la tinta,
      // que es lo único que no puede ser una clase porque depende del estado.
      style={({ isActive }) => ({
        color: isActive ? "rgb(var(--marco-tinta) / 1)" : "rgb(var(--marco-tinta) / 0.78)",
      })}
    >
      {/* 20px en navegación, 16px en tablas. Un solo grosor de trazo. */}
      <Icono size={20} strokeWidth={1.5} aria-hidden="true" />
      {soloIcono ? <span className="sr-only">{entrada.etiqueta}</span> : entrada.etiqueta}
    </NavLink>
  )
}

export function BarraLateral({
  enCajon = false,
  onNavegar,
}: {
  /**
   * Dentro del cajón del teléfono (`MenuMovil`). Ocupa todo el cajón y NO lleva
   * los `layoutId` del viaje desde el acceso: ese viaje termina en el menú fijo,
   * y dos elementos vivos con el mismo `layoutId` hacen que motion anime uno
   * hacia el otro.
   */
  enCajon?: boolean
  /** Al pulsar una entrada. El cajón se cierra con esto, también si se pulsa la
   *  ruta en la que ya se está, que no cambia la URL. */
  onNavegar?: () => void
} = {}) {
  const usuario = usarSesion((e) => e.usuario)
  const cliente = esCliente(usuario)
  const transportista = esTransportista(usuario)

  const consulta = useQuery({ ...CONSULTA_MENU, enabled: !cliente && !transportista })

  // El orden de las secciones y los departamentos sin módulo (28/10): los manda el
  // servidor (`accesos.ORDEN_SECCIONES`), la barra no los copia.
  const secciones2 = useQuery({
    queryKey: ["menu", "secciones"],
    queryFn: () => api.get<SeccionesMenu>("/api/v1/menu/secciones"),
    staleTime: 5 * 60_000,
    enabled: !cliente && !transportista,
  })

  const entradas = transportista
    ? TRANSPORTE
    : cliente
      ? PORTAL.filter((e) => tienePermiso(usuario, PERMISO_PORTAL[e.clave] ?? ""))
      : (consulta.data ?? [])

  // Agrupado conservando el orden que mandó el servidor: `Map` mantiene el orden de
  // inserción, y las filas vienen ya ordenadas por sección y posición.
  // ⚠ **El menú nace PLEGADO**: con catorce entradas en cinco secciones, abrirlo
  // entero obliga a recorrer una columna larga para llegar a lo de siempre. Por eso
  // se guarda lo ABIERTO —lo que cada quien decidió dejar a la vista— y no lo
  // plegado. Vive en el navegador, como el tema. Ver `lib/secciones.ts`.
  const [abiertas, setAbiertas] = useState<Set<string>>(leerAbiertas)
  // ⚠ **Colapsada a sólo iconos** (pedido el 10/10), para ganar ancho de trabajo. Sólo
  // en la barra fija: en el cajón del teléfono no tiene sentido, ya se esconde entera.
  const [colapsadaGuardada, setColapsada] = useState(leerColapsado)
  const colapsada = colapsadaGuardada && !enCajon

  function alternarColapso() {
    setColapsada((antes) => {
      guardarColapsado(!antes)
      return !antes
    })
  }

  function alternar(seccion: string) {
    setAbiertas((previas) => {
      const siguiente = new Set(previas)
      if (siguiente.has(seccion)) siguiente.delete(seccion)
      else siguiente.add(seccion)
      guardarAbiertas(siguiente)
      return siguiente
    })
  }

  const agrupadas = new Map<string, EntradaMenu[]>()
  for (const e of entradas) {
    const grupo = agrupadas.get(e.seccion)
    if (grupo === undefined) agrupadas.set(e.seccion, [e])
    else grupo.push(e)
  }
  const secciones = conDepartamentosVacios(agrupadas, secciones2.data)

  const barra = (
    /*
      ⚠ **Esta barra ES la tarjeta del acceso.** Comparte `layoutId` con la lámina
      del formulario, así que al entrar no aparece: se estrecha hasta estos 224 px y
      se estira hasta el alto de la pantalla. Ver `lib/transiciones.ts`.

      ⚠ **Va en cristal, y el área de trabajo NO.** Aquí detrás se ve pasar la
      fotografía; en el contenido no, porque ahí hay tablas de montos que se miran
      ocho horas y el contraste no puede depender de qué zona de la foto caiga
      debajo. Es la misma frontera que separa decoración de trabajo en el resto del
      sistema.
    */
    <motion.nav
      layoutId={enCajon ? undefined : LAMINA_SESION}
      transition={VIAJE_LAMINA}
      aria-label="Navegación principal"
      className={cn(
        "marco-cristal flex shrink-0 flex-col overflow-hidden rounded-[24px] shadow-[0_16px_40px_-20px_rgb(12_4_8/0.65)]",
        // El ancho cambia de golpe, sin animarlo: animar `width` recalcula el diseño
        // en cada fotograma (regla del frontend). Lo que se ve moverse es el área.
        enCajon ? "h-full w-full" : colapsada ? "w-16" : "w-56",
      )}
      onClick={(e) => {
        if (onNavegar !== undefined && (e.target as HTMLElement).closest("a") !== null) onNavegar()
      }}
      style={{
        // El menú crece HACIA ABAJO desde su borde superior, no desde el centro.
        // Sin esto, la lámina —que en el acceso está centrada en la pantalla— se
        // estira hacia los dos lados a la vez y el logotipo parece subir desde el
        // medio en vez de quedarse arriba, que es donde va a estar.
        transformOrigin: "top center",
      }}
    >
      {/* ⚠ **El logotipo va FUERA del área que se desplaza.** Estuvo dentro, y al
          bajar por el menú se iba con él: la marca desaparecía por arriba y el panel
          se quedaba sin cabecera, con las entradas cortadas contra el borde. Aquí es
          una cabecera fija y el desplazamiento empieza por debajo. */}
      <div className="shrink-0 px-3 pt-3">
        {/* El logotipo, arriba del todo. Termina aquí el viaje que empieza en el
          centro de la pantalla de acceso: el mismo `layoutId` que la marca de la
          secuencia, así que se encoge y sube en vez de desaparecer allí y aparecer
          aquí. */}
        {/* ⚠ **`preserve-aspect` y no el `layout` por omisión.** Con `layoutId`, motion
            interpola la CAJA de un extremo al otro, y aquí las dos cajas no tienen la
            misma proporción: en el acceso es ancha y baja —el logotipo con su descriptor
            debajo— y en el menú es casi cuadrada. Interpolando ancho y alto por separado,
            el contenido se estruja a mitad de camino y el logotipo se ve angosto durante
            el viaje aunque aterrice bien. Con esto, motion escala manteniendo la relación
            de aspecto y sólo compensa la diferencia con posición. */}
        <motion.div
          layoutId={enCajon ? undefined : MARCA_SESION}
          transition={VIAJE_LAMINA}
          layout="preserve-aspect"
          className="mb-3 flex justify-center px-2 py-1"
        >
          {/* Colapsada no cabe el wordmark: queda el «25», que es la marca. */}
          <LogoSetieca
            titulo="SETIECA · Agentes Aduanales, c.a."
            pieza={colapsada ? "numero" : "completo"}
            className={cn("w-auto text-[var(--marco-logo)]", colapsada ? "h-7" : "h-11")}
          />
        </motion.div>
        {/* El portal de transporte se distingue del ERP bajo la marca (25/10). */}
        {transportista && !colapsada && (
          <p
            className="-mt-2 mb-2 text-center font-marca text-[0.65rem] font-bold tracking-wide uppercase"
            style={{ color: "rgb(var(--marco-tinta) / 0.8)" }}
          >
            {NOMBRE_PORTAL_TRANSPORTE}
          </p>
        )}
      </div>

      {/* ⚠ **El que se desplaza es ESTE, no el panel.** El `overflow-y-auto` estaba
          en el mismo elemento que lleva el radio, así que el navegador pintaba la
          barra sobre la esquina redondeada — una franja recta cortando la curva, con
          aspecto de estar pegada por fuera. Aquí dentro, el `overflow-hidden` del
          panel la recorta con todo lo demás. */}
      <div
        className={cn(
          "barra-marco flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto pb-3",
          colapsada ? "px-2" : "px-3",
        )}
      >
        {/* Las entradas entran cuando la lámina ya casi ha llegado: durante el viaje
          la caja se está deformando, y un menú dentro de una caja que se deforma se
          ve estirado. Con el fade, lo que se deforma está vacío. */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ ...ENTRADA.menu, ease: "easeOut" }}
        >
          {colapsada
            ? // ⚠ **Colapsada, sin títulos ni secciones plegables**: todas las entradas,
              // con una línea entre secciones. Un icono dentro de una sección plegada
              // sería inalcanzable sin expandir la barra, que es lo que se quería evitar.
              [...secciones]
                .filter(([, grupo]) => grupo.length > 0)
                .map(([seccion, grupo], i) => (
                  <div
                    key={seccion || "sin-seccion"}
                    className={cn("flex flex-col gap-0.5", i > 0 && "mt-1 border-t pt-1")}
                    style={{ borderColor: "rgb(var(--marco-tinta) / 0.12)" }}
                  >
                    {grupo.map((e) => (
                      <Enlace key={e.clave} entrada={e} soloIcono={true} />
                    ))}
                  </div>
                ))
            : [...secciones].map(([seccion, grupo]) =>
                seccion === "" ? (
                  <div key="sin-seccion" className="mb-1">
                    {grupo.map((e) => (
                      <Enlace key={e.clave} entrada={e} />
                    ))}
                  </div>
                ) : grupo.length === 0 ? (
                  <DepartamentoSinModulo key={seccion} titulo={seccion} />
                ) : (
                  <Seccion
                    key={seccion}
                    titulo={seccion}
                    entradas={grupo}
                    plegada={!abiertas.has(seccion)}
                    onAlternar={() => alternar(seccion)}
                  />
                ),
              )}
        </motion.div>
      </div>
    </motion.nav>
  )

  if (enCajon) return barra
  // ⚠ **La aleta va FUERA de la barra, como en el 2.0** (pedido el 10/10): pegada a su
  // borde derecho y centrada en vertical. Dentro no se podría: la barra recorta lo que
  // sobresale (`overflow-hidden`, por el radio), y al pie del menú no se veía.
  return (
    <div className="relative flex shrink-0">
      {barra}
      <button
        type="button"
        onClick={alternarColapso}
        aria-pressed={colapsada}
        aria-label={colapsada ? "Expandir el menú" : "Colapsar el menú"}
        title={colapsada ? "Expandir el menú" : "Colapsar el menú"}
        className="marco-cristal foco-cristal toque-cristal absolute top-1/2 -right-3 z-10 flex h-14 w-[22px] -translate-y-1/2 items-center justify-center rounded-[9px]"
        style={{ color: "rgb(var(--marco-tinta) / 0.8)" }}
      >
        <ChevronLeft
          size={16}
          aria-hidden={true}
          className="transition-transform duration-200 motion-reduce:transition-none"
          style={{ transform: colapsada ? "rotate(180deg)" : undefined }}
        />
      </button>
    </div>
  )
}
